import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Wand2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useBrevoLists } from '../../queries/brevoLists';
import { useEmailPreview } from '../../queries/emailCampaigns';
import {
  MarketingCampaignEmail,
  useCreateMarketingCampaignEmail,
  useGenerateMarketingEmailDraft,
  useMarketingCampaignEmailRecipientCount,
  useSendMarketingCampaignEmail,
  useSendTestMarketingCampaignEmail,
  useUpdateMarketingCampaignEmail,
} from '../../queries/marketingCampaignsEmail';
import { HtmlCodeEditor } from '../../components/ui';
import { CampaignSendPanel } from './CampaignSendPanel';

interface MarketingCampaignFormDialogProps {
  campaign: MarketingCampaignEmail | null; // null = create
  initialAiContext?: string;
  // Pre-selected when arriving from a promo campaign, which already knows
  // which Brevo list carries its per-user PROMO_CODE contacts.
  initialListIds?: number[];
  onClose: () => void;
}

export function MarketingCampaignFormDialog({ campaign, initialAiContext, initialListIds, onClose }: MarketingCampaignFormDialogProps) {
  const [name, setName] = useState(campaign?.campaign_name ?? '');
  const [campaignType, setCampaignType] = useState(campaign?.campaign_type ?? 'marketing');
  const [subject, setSubject] = useState(campaign?.subject_line ?? '');
  const [html, setHtml] = useState(campaign?.html_body ?? '');
  const [text, setText] = useState(campaign?.text_body ?? '');
  const [aiContext, setAiContext] = useState(initialAiContext ?? campaign?.ai_context ?? '');
  const [selectedListIds, setSelectedListIds] = useState<number[]>(campaign?.brevo_list_ids ?? initialListIds ?? []);
  // Stable id for this campaign across the generate → save round-trip, so n8n's
  // upsert and the saved row reference the same campaign_id (matches old admin).
  const campaignId = useMemo(
    () => campaign?.campaign_id ?? `draft-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    [campaign],
  );
  // Opens on the rendered email, same as the tips-email dialog. An empty
  // body falls through to the editor below, so creating still starts there.
  const [showPreview, setShowPreview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendTestError, setSendTestError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const { data: lists = [] } = useBrevoLists();
  const create = useCreateMarketingCampaignEmail();
  const update = useUpdateMarketingCampaignEmail();
  const generate = useGenerateMarketingEmailDraft();
  const sendTest = useSendTestMarketingCampaignEmail();
  const send = useSendMarketingCampaignEmail();
  const { data: recipientCount } = useMarketingCampaignEmailRecipientCount(campaign?.id ?? 0, selectedListIds);
  // Only fetched while the preview is shown; renders the branded header/footer.
  const { data: preview } = useEmailPreview(subject, html, showPreview);

  const canSave = name.trim() && subject.trim() && html.trim();

  const handleGenerate = () => {
    if (!aiContext.trim()) return;
    generate.mutate(
      { context: aiContext, campaign_id: campaignId, campaign_name: name || 'Untitled Campaign', campaign_type: campaignType },
      {
        onSuccess: (result) => {
          setSubject(result.subject);
          setHtml(result.html);
          setText(result.text);
        },
      },
    );
  };

  const handleSave = () => {
    setError(null);
    const draft = {
      campaign_name: name,
      campaign_type: campaignType,
      subject_line: subject,
      html_body: html,
      text_body: text || null,
      ai_context: aiContext || null,
      brevo_list_ids: selectedListIds,
      campaign_id: campaignId,
    };
    const onSettled = { onSuccess: onClose, onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')) };
    if (campaign) {
      update.mutate({ ...draft, id: campaign.id }, onSettled);
    } else {
      create.mutate(draft, onSettled);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-lg font-semibold">{campaign ? 'Edit marketing campaign' : 'Create marketing campaign'}</Dialog.Title>

          {/* Available when creating AND when editing a not-yet-sent campaign, so
              content can be regenerated from an updated context (matches old admin). */}
          {campaign?.status !== 'sent' && (
            <div className="mt-4 rounded-md border border-dashed border-input p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Wand2 className="h-4 w-4" />
                {campaign ? 'AI content generation' : 'Generate with AI'}
              </p>
              <textarea
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="Describe the campaign context…"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generate.isPending || !aiContext.trim()}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                {generate.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {campaign ? 'Regenerate content' : 'Generate via n8n'}
              </button>
              {generate.isError && <p className="mt-1 text-xs text-destructive">{extractErrorMessage(generate.error, 'Generation failed.')}</p>}
            </div>
          )}

          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="mc-name" className="mb-1 block text-sm font-medium">Campaign name</label>
              <input
                id="mc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={campaign?.status === 'sent'}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="mc-type" className="mb-1 block text-sm font-medium">Campaign type</label>
              <select
                id="mc-type"
                value={campaignType}
                onChange={(e) => setCampaignType(e.target.value)}
                disabled={campaign?.status === 'sent'}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="marketing">Marketing</option>
                <option value="promotion">Promotion</option>
                <option value="announcement">Announcement</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </div>
            <div>
              <label htmlFor="mc-subject" className="mb-1 block text-sm font-medium">Subject line</label>
              <input
                id="mc-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={campaign?.status === 'sent'}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                {showPreview && html.trim() ? (
                  <span className="block text-sm font-medium">Email</span>
                ) : (
                  <label htmlFor="mc-html" className="block text-sm font-medium">HTML body</label>
                )}
                {html.trim() && (
                  <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    {showPreview ? 'Edit HTML' : 'Preview email'}
                  </button>
                )}
              </div>
              {showPreview && html.trim() ? (
                // Shows the branded header/footer wrapper recipients receive.
                // The raw body renders immediately and is replaced by the
                // branded version when it arrives. Chrome does not reliably
                // re-navigate a srcdoc document when the attribute is mutated,
                // so that swap remounts the element (hence the key).
                // allow-same-origin rather than sandbox="" so it renders at
                // all; with no allow-scripts nothing in the email can run.
                <iframe
                  key={preview?.html ? 'branded' : 'raw'}
                  title="Email preview"
                  srcDoc={preview?.html ?? html}
                  sandbox="allow-same-origin"
                  className="h-96 w-full rounded-md border border-border bg-white"
                />
              ) : (
                <HtmlCodeEditor
                  id="mc-html"
                  value={html}
                  onChange={setHtml}
                  readOnly={campaign?.status === 'sent'}
                  height="16rem"
                />
              )}
            </div>
            <div>
              <label htmlFor="mc-text" className="mb-1 block text-sm font-medium">Text body (optional, auto-generated if blank)</label>
              <textarea
                id="mc-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={campaign?.status === 'sent'}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              {campaign ? 'Close' : 'Cancel'}
            </button>
            {campaign?.status !== 'sent' && (
              <button
                type="button"
                onClick={handleSave}
                disabled={create.isPending || update.isPending || !canSave}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {(create.isPending || update.isPending) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
            )}
          </div>

          {campaign && (
            <div className="mt-4">
              <CampaignSendPanel
                status={campaign.status}
                testRequired={false}
                testPassedAt={campaign.metadata?.testPassedAt}
                listOptions={lists}
                selectedListIds={selectedListIds}
                onSelectedListIdsChange={setSelectedListIds}
                recipientCount={recipientCount?.count}
                onSendTest={(testEmails) => {
                  setSendTestError(null);
                  sendTest.mutate(
                    { id: campaign.id, testEmails, listIds: selectedListIds },
                    { onError: (err) => setSendTestError(extractErrorMessage(err, 'Test send failed.')) },
                  );
                }}
                sendTestPending={sendTest.isPending}
                sendTestError={sendTestError}
                onSend={(overrideSent) => {
                  setSendError(null);
                  send.mutate(
                    { id: campaign.id, listIds: selectedListIds, overrideSent },
                    { onError: (err) => setSendError(extractErrorMessage(err, 'Send failed.')) },
                  );
                }}
                sendPending={send.isPending}
                sendError={sendError}
              />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
